import 'core-js/stable'
import 'regenerator-runtime/runtime'

import { createApp } from 'vue'
import App from './App'
import router from './router'
import CoreuiVue from '@coreui/vue'
import CIcon from '@coreui/icons-vue'
import { iconsSet as icons } from './assets/icons/icons.js'
import settings from '../public/settings.json'
import { createPinia } from 'pinia'
import axios from 'axios'
import { createHead } from '@vueuse/head'
import './assets/scss/tailwind.css'
import './assets/styles/theme-colors.css'

import '@coreui/coreui/dist/css/coreui.min.css'

import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

import PulseLoader from 'vue-spinner/src/PulseLoader.vue'

const app = createApp(App)
const pinia = createPinia()
const head = createHead()

app.use(pinia)
app.use(router)
app.use(CoreuiVue)
app.use(head)

app.component('VSelect', vSelect)
app.component('PulseLoader', PulseLoader)
app.component('CIcon', CIcon)

const baseAPI = axios.create({
  baseURL: process.env.VUE_APP_API_URL || settings.apiURL,
  withCredentials: true,
})

const UNAUTHORIZED = 401
const PUBLIC_ROUTES = ['/', '/login', '/daftar', '/ranking', '/cari', '/pemain']

baseAPI.interceptors.response.use(response => response, error => {
  const status = error.response && error.response.status
  const currentRoute = router.currentRoute?.value || {}
  const lowerPath = (currentRoute.path || '').toLowerCase()
  const isPublicRoute = PUBLIC_ROUTES.some(path =>
    path === '/' ? lowerPath === '/' : lowerPath.startsWith(path)
  )
  const isLoginPage = lowerPath.startsWith('/login')
  const isMatchGroupPage = lowerPath.startsWith('/matchgroup')
  const isDashboardPage = lowerPath.startsWith('/dashboard')
  const isMyTournamentsPage = lowerPath.startsWith('/mytournaments')

  // Don't auto-redirect on 401 for authenticated pages - let them handle it
  if (status === UNAUTHORIZED && !isPublicRoute && !isLoginPage && !isMatchGroupPage && !isDashboardPage && !isMyTournamentsPage) {
    router.push('/Login')
  }

  return Promise.reject(error)
})

app.config.globalProperties.$API = baseAPI
app.config.globalProperties.$log = console.log.bind(console)
app.config.globalProperties.GetBaseUrl = function () {
  return process.env.VUE_APP_API_URL || settings.apiURL
}
app.config.globalProperties.GoToLogin = function () {
  const path = router.currentRoute?.value?.path || ''
  if (path.toLowerCase() !== '/login' && path.toLowerCase() !== '/loginaccount') {
    router.push('/LoginAccount')
  }
}
// UserAuth — checks cookie set on login; used by mounted() guards across all views
app.config.globalProperties.UserAuth = function () {
  const cookie = document.cookie.split(';').find(c => c.trim().startsWith('UserId='))
  const userId = cookie ? cookie.split('=')[1]?.trim() : null
  if (userId && userId !== '0') return Promise.resolve(true)
  const stored = localStorage.getItem('sc_user')
  if (stored) return Promise.resolve(true)
  return Promise.resolve(false)
}

app.config.globalProperties.SignOut = function () {
  return baseAPI
    .get('sportcenter/Insert/SignOut', { withCredentials: true })
    .then(response => {
      if (response.status === 200) {
        localStorage.removeItem('sc_token')
        localStorage.removeItem('sc_user')
        router.push('/')
      }
    })
}

// SportCenter.Pro API endpoints
// Auth
app.config.globalProperties.$Login = 'sportcenter/Insert/Login'
app.config.globalProperties.$Register = 'sportcenter/Insert/Register'
app.config.globalProperties.$SignOut = 'sportcenter/Insert/SignOut'
app.config.globalProperties.$UserAuth = 'sportcenter/Insert/IsAuthenticated'
app.config.globalProperties.$ForgotPassword = 'sportcenter/Insert/ForgotPassword'
app.config.globalProperties.$VerifyEmail = 'sportcenter/Insert/VerifyEmail'
app.config.globalProperties.$ResetPassword = 'sportcenter/Insert/ResetPassword'
app.config.globalProperties.$ValidateForgotPasswordCode = 'sportcenter/Insert/ValidateForgotPasswordCode'
app.config.globalProperties.$ChangePassword = 'sportcenter/Insert/ChangePassword'
app.config.globalProperties.$GoogleLogin = 'sportcenter/Insert/GoogleLogin'

// Player
app.config.globalProperties.$GetPlayerProfile = 'sportcenter/Read/PlayerProfile'
app.config.globalProperties.$UpdatePlayerProfile = 'kiddo/Update/UpdatePlayerProfile'
app.config.globalProperties.$GetPlayerHistory = 'sportcenter/Read/MatchHistory'
app.config.globalProperties.$GetNotifications = 'sportcenter/Read/Notifications'
app.config.globalProperties.$MarkNotificationRead = 'sportcenter/Update/NotificationRead'

// Ranking
app.config.globalProperties.$GetRanking = 'sportcenter/Read/Ranking'
app.config.globalProperties.$GetTopPlayers = 'sportcenter/Read/TopPlayers'
app.config.globalProperties.$SearchPlayers = 'sportcenter/Read/SearchPlayers'
app.config.globalProperties.$GetPublicProfile = 'sportcenter/Read/PublicProfile'

// Match / Game
app.config.globalProperties.$GenerateQR = 'sportcenter/Insert/GenerateQR'
app.config.globalProperties.$AcceptChallenge = 'sportcenter/Insert/AcceptQR'
app.config.globalProperties.$RejectChallenge = 'sportcenter/Insert/RejectChallenge'
app.config.globalProperties.$SubmitResult = 'sportcenter/Insert/SubmitResult'
app.config.globalProperties.$ConfirmResult = 'sportcenter/Insert/ConfirmResult'
app.config.globalProperties.$DisputeResult = 'sportcenter/Insert/DisputeResult'
app.config.globalProperties.$GetMatchDetail = 'sportcenter/Read/MatchDetail'
app.config.globalProperties.$GetActiveMatch = 'sportcenter/Read/ActiveMatch'

// Event
app.config.globalProperties.$RedeemCode       = 'sportcenter/Insert/RedeemCode'
app.config.globalProperties.$CreateEvent      = 'sportcenter/Insert/CreateEvent'
app.config.globalProperties.$JoinEvent        = 'sportcenter/Insert/JoinEvent'
app.config.globalProperties.$CloseEvent       = 'sportcenter/Insert/CloseEvent'
app.config.globalProperties.$MyEvents         = 'sportcenter/Read/MyEvents'
app.config.globalProperties.$EventDetail      = 'sportcenter/Read/EventDetail'
app.config.globalProperties.$EventRanking     = 'sportcenter/Read/EventRanking'

// Tournament
app.config.globalProperties.$AutoDrawGroups      = 'sportcenter/Insert/AutoDrawGroups'
app.config.globalProperties.$GenerateKnockout    = 'sportcenter/Insert/GenerateKnockout'
app.config.globalProperties.$GetKnockoutBracket  = 'sportcenter/Read/KnockoutBracket'
app.config.globalProperties.$SubmitKnockoutScore = 'sportcenter/Insert/SubmitKnockoutScore'
app.config.globalProperties.$SaveMatchGames = 'sportcenter/Insert/SaveMatchGames'
app.config.globalProperties.$ClearMatchGames = 'sportcenter/Insert/ClearMatchGames'
app.config.globalProperties.$GetGroupStandings      = 'sportcenter/Read/GroupStandings'
app.config.globalProperties.$AdvanceKnockoutRound   = 'sportcenter/Insert/AdvanceKnockoutRound'
app.config.globalProperties.$ResetTournamentResults  = 'sportcenter/Insert/ResetTournamentResults'
app.config.globalProperties.$TournamentPublicView      = 'sportcenter/Read/TournamentPublicView'
app.config.globalProperties.$UpdateMatchSchedule       = 'sportcenter/Insert/UpdateMatchSchedule'
app.config.globalProperties.$TournamentDiscovery       = 'sportcenter/Read/TournamentDiscovery'
app.config.globalProperties.$MyTournaments             = 'sportcenter/Read/MyTournaments'
app.config.globalProperties.$UpdateRegistrationStatus  = 'kiddo/Insert/UpdateRegistrationStatus'
app.config.globalProperties.$DeleteRegistrations       = 'sportcenter/Insert/DeleteRegistrations'
app.config.globalProperties.$UpdateTeamName            = 'sportcenter/Insert/UpdateTeamName'
app.config.globalProperties.$SwapKnockoutTeams         = 'sportcenter/Insert/SwapKnockoutTeams'
app.config.globalProperties.$GetAllRegistrationsByTournamentId = 'kiddo/Read/GetAllRegistrationsByTournamentId'
app.config.globalProperties.$GetDeletedRegistrations           = 'kiddo/Read/GetDeletedRegistrationsByTournamentId'
app.config.globalProperties.$UploadTournament          = 'sportcenter/Insert/UploadTournament'
app.config.globalProperties.$UpdateTournament          = 'sportcenter/Insert/UpdateTournament'
app.config.globalProperties.$DeleteTournament          = 'sportcenter/Insert/DeleteTournament'
app.config.globalProperties.$InsertRegistrationTeam    = 'kiddo/Insert/InsertRegistrationTeam'
app.config.globalProperties.$GetTeamByToken            = 'kiddo/Read/GetTeamByToken'
app.config.globalProperties.$JoinTeamByToken           = 'kiddo/Insert/JoinTeamByToken'
app.config.globalProperties.$CheckIfUserInTeam         = 'kiddo/Read/CheckIfUserInTeam'
app.config.globalProperties.$GetUserProfile            = 'kiddo/Read/GetUserProfile'
app.config.globalProperties.$GetTeamMembers            = 'kiddo/Read/GetTeamMembers'
app.config.globalProperties.$ApproveTeamMember         = 'kiddo/Insert/ApproveTeamMember'
app.config.globalProperties.$RejectTeamMember          = 'kiddo/Insert/RejectTeamMember'
app.config.globalProperties.$GetMyRegistrations        = 'kiddo/Read/GetMyRegistrations'
app.config.globalProperties.$GetSportCategories        = 'kiddo/Read/GetSportCategories'

// Admin
app.config.globalProperties.$AdminGetPlayers = 'sportcenter/Read/AdminPlayers'
app.config.globalProperties.$AdminGetMatches = 'sportcenter/Read/AdminMatches'
app.config.globalProperties.$AdminGetDisputes = 'sportcenter/Read/AdminDisputes'
app.config.globalProperties.$AdminResolveDispute = 'sportcenter/Insert/AdminResolveDispute'
app.config.globalProperties.$AdminUpdateElo = 'sportcenter/Insert/AdminAdjustElo'
app.config.globalProperties.$AdminGetStats = 'sportcenter/Read/AdminStats'
app.config.globalProperties.$AdminGetSettings = 'sportcenter/Read/AdminSettings'
app.config.globalProperties.$AdminUpdateSettings = 'sportcenter/Insert/AdminUpdateSettings'
app.config.globalProperties.$AdminGenerateCode = 'sportcenter/Insert/AdminGenerateCode'
app.config.globalProperties.$AdminGetCodes     = 'sportcenter/Read/AdminCodes'
app.config.globalProperties.$AdminGetEvents    = 'sportcenter/Read/AdminEvents'
app.config.globalProperties.$AdminGetTournaments = 'sportcenter/Read/AdminTournaments'
app.config.globalProperties.$AdminDeleteTournament = 'sportcenter/Insert/AdminDeleteTournament'
app.config.globalProperties.$AdminArchiveTournament = 'sportcenter/Insert/AdminArchiveTournament'
app.config.globalProperties.$AdminUnarchiveTournament = 'sportcenter/Insert/AdminUnarchiveTournament'
app.config.globalProperties.$AdminFeatureTournament = 'sportcenter/Insert/AdminFeatureTournament'
app.config.globalProperties.$AdminUnfeatureTournament = 'sportcenter/Insert/AdminUnfeatureTournament'
app.config.globalProperties.$FeaturedTournaments = 'sportcenter/Read/FeaturedTournaments'
app.config.globalProperties.$AnalyticsShowcase = 'sportcenter/Read/AnalyticsShowcase'

// Aliases for convenience
app.config.globalProperties.$GetActiveMatches = 'sportcenter/Read/ActiveMatch'
app.config.globalProperties.$GetMatchHistory  = 'sportcenter/Read/MatchHistory'
app.config.globalProperties.$GetEloProfile    = 'sportcenter/Read/EloProfile'

// Legacy kiddo routes (used by non-SportCenter views)
app.config.globalProperties.$LoginUserDetail             = 'sportcenter/Read/EloProfile'
app.config.globalProperties.$GetListTournamentPublished  = 'kiddo/Read/GetListTournamentPublished'
app.config.globalProperties.$GetListTournamentByUserId   = 'kiddo/Read/GetListTournamentByUserId'
app.config.globalProperties.$GetListTournamentByCategory = 'kiddo/Read/GetListTournamentByCategory'
app.config.globalProperties.$GetListTourById             = 'kiddo/Read/GetListTournamentById'
app.config.globalProperties.$GetListParticipant          = 'kiddo/Read/GetListParticipant'
app.config.globalProperties.$GetListParticipantByTournamentId = 'kiddo/Read/GetListParticipantByTournamentId'
app.config.globalProperties.$GetListParticipantByOrganizerUserId = 'kiddo/Read/GetListParticipantByOrganizerUserId'
app.config.globalProperties.$GetTeamRegistrationById     = 'kiddo/Read/GetTeamRegistrationById'
app.config.globalProperties.$GetListTeam                 = 'kiddo/Read/GetListTeam'
app.config.globalProperties.$GetListTeamById             = 'kiddo/Read/GetListTeamById'
app.config.globalProperties.$GetListTeamGroup            = 'kiddo/Read/GetListTeamGroup'
app.config.globalProperties.$GetListTeamGroupbyCategory  = 'kiddo/Read/GetListTeamGroupByCategory'
app.config.globalProperties.$GetListTeambyCategory       = 'kiddo/Read/GetListTeambyCategory'
app.config.globalProperties.$GetListMatchTeam            = 'kiddo/Read/GetListMatchTeam'
app.config.globalProperties.$GetListMatchTeamById        = 'kiddo/Read/GetListMatchTeamById'
app.config.globalProperties.$GetListMatchTeambyCategory  = 'kiddo/Read/GetListMatchTeambyCategory'
app.config.globalProperties.$GetListMatchTeamGroupAById  = 'kiddo/Read/GetListMatchTeamGroupAById'
app.config.globalProperties.$GetListMatchTeamGroupBById  = 'kiddo/Read/GetListMatchTeamGroupBById'
app.config.globalProperties.$GetListMatchTeamScore       = 'kiddo/Read/GetListMatchTeamScore'
app.config.globalProperties.$GetListSportCategory        = 'kiddo/Read/GetListSportCategory'
app.config.globalProperties.$GetListTypeGroup            = 'kiddo/Read/GetListTypeGroup'
app.config.globalProperties.$GetListTypePlayer           = 'kiddo/Read/GetListTypePlayer'
app.config.globalProperties.$GetListLevel                = 'kiddo/Read/GetListLevel'
app.config.globalProperties.$GetListClub                 = 'kiddo/Read/GetListClub'
app.config.globalProperties.$GetListAnnouncement         = 'kiddo/Read/GetListAnnouncement'
app.config.globalProperties.$GetListBootcamp             = 'kiddo/Read/GetListBootcamp'
app.config.globalProperties.$GetListBootcampById         = 'kiddo/Read/GetListBootcampById'
app.config.globalProperties.$GetListBootcampByIdFirst    = 'kiddo/Read/GetListBootcampByIdFirst'
app.config.globalProperties.$GetListVideoJustForYou      = 'kiddo/Read/GetListVideoJustForYou'
app.config.globalProperties.$GetListPackageFeeClub       = 'kiddo/Read/GetListPackageFeeClub'
app.config.globalProperties.$GetListteacher              = 'kiddo/Read/GetListteacher'
app.config.globalProperties.$GetListConfirmChosenTeam    = 'kiddo/Read/GetListConfirmChosenTeam'
app.config.globalProperties.$GetListPendingChosenTeam    = 'kiddo/Read/GetListPendingChosenTeam'
app.config.globalProperties.$GetListPendingChosenTeamById= 'kiddo/Read/GetListPendingChosenTeamById'
app.config.globalProperties.$GetDetailChosenTeam         = 'kiddo/Read/GetDetailChosenTeam'
app.config.globalProperties.$GetListTournamentByTeamIds  = 'kiddo/Read/GetListTournamentByTeamIds'
app.config.globalProperties.$GetRequirement              = 'kiddo/Read/GetRequirement'
app.config.globalProperties.$GetHashTag                  = 'kiddo/Read/GetHashTag'
app.config.globalProperties.$GetHighlight                = 'kiddo/Read/GetHighlight'
app.config.globalProperties.$GetWhatYouLearn             = 'kiddo/Read/GetWhatYouLearn'
app.config.globalProperties.$GetTokenEmail               = 'kiddo/Read/GetTokenEmail'
app.config.globalProperties.$DeleteBootcamp              = 'kiddo/Delete/DeleteBootcamp'
app.config.globalProperties.$DeleteProfilePhoto          = 'kiddo/Delete/DeleteProfilePhoto'
app.config.globalProperties.$DeleteTeamGroup             = 'kiddo/Delete/DeleteTeamGroup'
app.config.globalProperties.$DeleteTeamMatchGroup        = 'kiddo/Delete/DeleteTeamMatchGroup'

app.provide('icons', icons)
app.mount('#app')
